// edu_platform/frontend/src/components/TopicSelect.jsx
import React, { useState, useEffect } from 'react';
import API from '../api/axiosConfig';

const TopicSelect = ({ selectedSubjectId, value, onChange, currentUser, className, onDisabledClick }) => {
  const [topics, setTopics] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [newTopicName, setNewTopicName] = useState('');
  const [editingTopicId, setEditingTopicId] = useState(null);
  const [editingName, setEditingName] = useState('');

  const isAdmin = currentUser?.is_staff || currentUser?.is_superuser;

  useEffect(() => {
    if (selectedSubjectId) {
      fetchTopics();
    } else {
      setTopics([]);
    }
  }, [selectedSubjectId]);

  const fetchTopics = async () => {
    try {
      const response = await API.get(`/api/topics/?subject=${selectedSubjectId}`);
      setTopics(response.data);
    } catch (error) {
      console.error("Ошибка при загрузке тем:", error);
    }
  };

  const handleAddTopic = async (e) => {
    e.stopPropagation();
    if (!newTopicName.trim() || !selectedSubjectId) return;

    try {
      const response = await API.post('/api/topics/', { name: newTopicName, subject: selectedSubjectId });
      setTopics([...topics, response.data]);
      setNewTopicName('');
    } catch (error) {
      alert("Ошибка при добавлении темы.");
    }
  };

  const handleDeleteTopic = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Удалить эту тему?")) return;

    try {
      await API.delete(`/api/topics/${id}/`);
      setTopics(topics.filter(t => t.id !== id));
      if (value === id) onChange(''); 
    } catch (error) {
      alert("Ошибка при удалении.");
    }
  };

  const startEdit = (topic, e) => {
    e.stopPropagation();
    setEditingTopicId(topic.id);
    setEditingName(topic.name);
  };

  const handleSaveEdit = async (id, e) => {
    e.stopPropagation();
    if (!editingName.trim()) return;

    try {
      const response = await API.patch(`/api/topics/${id}/`, { name: editingName });
      setTopics(topics.map(t => t.id === id ? response.data : t));
      setEditingTopicId(null);
    } catch (error) {
      alert("Ошибка при изменении темы.");
    }
  };

  // ЕСЛИ ПРЕДМЕТ НЕ ВЫБРАН - ПОКАЗЫВАЕМ КЛИКАБЕЛЬНУЮ ЗАГЛУШКУ
  if (!selectedSubjectId) {
    return (
      <div 
        className={`${className} flex justify-between items-center text-left opacity-70 cursor-pointer`}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (onDisabledClick) onDisabledClick(); // Вызываем уведомление
        }}
      >
        <span className="truncate pr-2">Сначала выберите предмет...</span>
        <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
      </div>
    );
  }

  return (
    <div className="relative w-full h-full">
      <button
        type="button"
        className={`${className} flex justify-between items-center text-left w-full h-full`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="truncate pr-2">
          {topics.find(t => t.id === value)?.name || "Выберите тему..."}
        </span>
        <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
      </button>

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden" style={{ maxHeight: '250px' }}>
          <div className="overflow-y-auto max-h-[200px] custom-scrollbar">
              {topics.length === 0 ? (
                <div className="p-3 text-xs text-gray-500 italic text-center">Темы еще не созданы</div>
              ) : (
                <div className="py-1">
                  {topics.map(topic => (
                    <div 
                      key={topic.id} 
                      className={`flex justify-between items-center px-3 py-2 text-xs cursor-pointer transition-colors ${value === topic.id ? 'bg-blue-50 font-bold text-blue-700' : 'text-gray-700 hover:bg-gray-50'}`}
                      onClick={() => { onChange(topic.id); setIsOpen(false); }}
                    >
                      {editingTopicId === topic.id ? (
                        <div className="flex w-full gap-1" onClick={(e) => e.stopPropagation()}>
                          <input type="text" className="w-full px-2 py-1 text-xs border border-blue-300 rounded outline-none" value={editingName} onChange={(e) => setEditingName(e.target.value)} />
                          <button className="px-2 py-1 text-white bg-green-500 rounded hover:bg-green-600" onClick={(e) => handleSaveEdit(topic.id, e)}>✓</button>
                          <button className="px-2 py-1 text-white bg-gray-400 rounded hover:bg-gray-500" onClick={() => setEditingTopicId(null)}>✕</button>
                        </div>
                      ) : (
                        <>
                          <span className="truncate">{topic.name}</span>
                          {isAdmin && (
                            <div className="flex gap-1 shrink-0 ml-2" onClick={(e) => e.stopPropagation()}>
                              <button className="text-yellow-600 hover:bg-yellow-100 p-1 rounded" title="Изменить" onClick={(e) => startEdit(topic, e)}>✏️</button>
                              <button className="text-red-600 hover:bg-red-100 p-1 rounded" title="Удалить" onClick={(e) => handleDeleteTopic(topic.id, e)}>🗑️</button>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  ))}
                </div>
              )}
          </div>

          <div className="p-2 border-t border-gray-100 bg-gray-50 sticky bottom-0">
            <div className="flex gap-1">
              <input
                type="text"
                className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded outline-none focus:border-blue-500"
                placeholder="Новая тема..."
                value={newTopicName}
                onChange={(e) => setNewTopicName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAddTopic(e)}
              />
              <button className="px-3 py-1.5 text-white bg-blue-600 rounded hover:bg-blue-700 font-bold" type="button" onClick={handleAddTopic}>+</button>
            </div>
          </div>
        </div>
      )}
      
      {isOpen && <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />}
    </div>
  );
};

export default TopicSelect;